import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Hero() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [pickupLocation, setPickupLocation] = useState('');
  const [dropoffLocation, setDropoffLocation] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');

  const handleSearch = () => {
    try {
      // Validate required fields
      if (!pickupLocation || !pickupDate || !dropoffDate) {
        alert('Please fill in all required fields');
        return;
      }
      
      // Validate dates
      if (new Date(pickupDate) >= new Date(dropoffDate)) {
        alert('Return date must be after pickup date');
        return;
      }
      
      // Navigate to fleet page with search parameters
      const searchParams = new URLSearchParams({
        pickup: pickupLocation,
        dropoff: dropoffLocation || pickupLocation,
        pickupDate: pickupDate,
        dropoffDate: dropoffDate
      });
      
      window.location.href = `/fleet?${searchParams.toString()}`;
    } catch (error) {
      console.error('Navigation error:', error);
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="relative min-h-screen bg-cover bg-center bg-no-repeat" style={{ backgroundImage: 'url(https://public.readdy.ai/ai/img_res/2e5b12c3ac72b82d5ac4787a442c823d.jpg)' }}>
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent"></div>
      
      {/* Header */}
      <header className="relative z-20 bg-white shadow-sm">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-between h-24">
            {/* Logo - Left Side */}
            <Link to="/" className="flex items-center cursor-pointer">
              <h1>
                <img 
                  src="https://static.readdy.ai/image/7f27f7e05a988155f4210dfe443a01a1/8cc755d4eae113568d97fe7c581a8634.png" 
                  alt="Car2Go Logo" 
                  className="h-20 w-auto object-contain"
                  style={{ imageRendering: '-webkit-optimize-contrast' }}
                  loading="eager"
                />
              </h1>
            </Link>

            {/* Navigation - Right Side */}
            <div className="flex items-center space-x-8">
              <nav className="hidden md:flex items-center space-x-8">
                <Link to="/" className="text-blue-600 font-semibold cursor-pointer">Home</Link>
                <Link to="/fleet" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer">Fleet</Link>
                <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer">Locations</a>
                <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer">Corporate</a>
                <Link to="/contact" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer">Contact</Link>
              </nav>

              <button className="hidden md:block bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-6 rounded-lg transition-colors duration-200 whitespace-nowrap cursor-pointer">
                Book Now
              </button>
              
              <button 
                className="md:hidden text-gray-600 cursor-pointer"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
              >
                <i className={`ri-${isMenuOpen ? 'close' : 'menu'}-line text-2xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              <nav className="flex flex-col space-y-4">
                <Link to="/" className="text-blue-600 font-semibold cursor-pointer">Home</Link>
                <Link to="/fleet" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer">Fleet</Link>
                <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer">Locations</a>
                <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer">Corporate</a>
                <Link to="/contact" className="text-gray-600 hover:text-blue-600 transition-colors duration-200 cursor-pointer">Contact</Link>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Content */}
      <section className="relative flex items-center justify-center min-h-screen">
        <div className="container mx-auto px-6 text-center text-white">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col items-center mb-8">
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                <span className="text-white">Professional Car Rental</span>
              </h1>
            </div>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 leading-relaxed">
              New Jersey's trusted car rental company since 2021, serving business and leisure travelers. 
              <br />Premium vehicles, professional service, competitive rates.
            </p>
            
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8 shadow-2xl max-w-5xl mx-auto mb-8">
              <h3 className="text-2xl font-semibold text-gray-800 mb-6">Reserve Your Vehicle</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <label htmlFor="pickup-location" className="block text-sm font-medium text-gray-700">Pickup Location</label>
                  <div className="relative">
                    <i className="ri-map-pin-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      id="pickup-location"
                      type="text"
                      value={pickupLocation}
                      onChange={(e) => setPickupLocation(e.target.value)}
                      placeholder="Enter city or airport"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="dropoff-location" className="block text-sm font-medium text-gray-700">Return Location</label>
                  <div className="relative">
                    <i className="ri-map-pin-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      id="dropoff-location"
                      type="text"
                      value={dropoffLocation}
                      onChange={(e) => setDropoffLocation(e.target.value)}
                      placeholder="Same as pickup"
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="pickup-date" className="block text-sm font-medium text-gray-700">Pickup Date</label>
                  <div className="relative">
                    <i className="ri-calendar-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      id="pickup-date"
                      type="date"
                      value={pickupDate}
                      onChange={(e) => setPickupDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label htmlFor="dropoff-date" className="block text-sm font-medium text-gray-700">Return Date</label>
                  <div className="relative">
                    <i className="ri-calendar-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      id="dropoff-date"
                      type="date"
                      value={dropoffDate}
                      onChange={(e) => setDropoffDate(e.target.value)}
                      min={pickupDate || new Date().toISOString().split('T')[0]}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleSearch}
                className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-8 rounded-lg transition-colors duration-200 text-lg whitespace-nowrap cursor-pointer"
                aria-label="Search available vehicles"
              >
                Search Available Vehicles
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <i className="ri-building-line text-4xl text-blue-400 mb-3"></i>
                <h3 className="text-lg font-semibold mb-2">Licensed in New Jersey</h3>
                <p className="text-gray-200">Registered car rental company serving the tri-state area</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <i className="ri-map-2-line text-4xl text-blue-400 mb-3"></i>
                <h3 className="text-lg font-semibold mb-2">Regional Coverage</h3>
                <p className="text-gray-200">Serving New Jersey and surrounding metropolitan areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                <i className="ri-customer-service-2-line text-4xl text-blue-400 mb-3"></i>
                <h3 className="text-lg font-semibold mb-2">24/7 Support</h3>
                <p className="text-gray-200">Professional customer service whenever you need us</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
